import { timezone } from 'core/util/dateUtils';

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;
const SECONDS_PER_WEEK = 604800;
const SECONDS_PER_YEAR = 31536000;

// Break elapsed seconds into year/day/hour/minute/second parts.
function decomposeElapsed(totalSec) {
  let remainingSec = Math.max(0, Math.floor(totalSec));

  const years = Math.floor(remainingSec / SECONDS_PER_YEAR);
  remainingSec -= years * SECONDS_PER_YEAR;

  const days = Math.floor(remainingSec / SECONDS_PER_DAY);
  remainingSec -= days * SECONDS_PER_DAY;

  const hours = Math.floor(remainingSec / SECONDS_PER_HOUR);
  remainingSec -= hours * SECONDS_PER_HOUR;

  const minutes = Math.floor(remainingSec / SECONDS_PER_MINUTE);
  remainingSec -= minutes * SECONDS_PER_MINUTE;

  return {
    years,
    days,
    hours,
    minutes,
    seconds: remainingSec
  };
}

// Return non-zero duration units ordered from largest to smallest.
function getNonZeroDurationUnits(totalSec) {
  const { years, days, hours, minutes, seconds } = decomposeElapsed(totalSec);
  return [
    [years, 'y'],
    [days, 'd'],
    [hours, 'h'],
    [minutes, 'm'],
    [seconds, 's']
  ].filter(([value]) => value > 0);
}

// Format elapsed seconds as a single compact unit (s, m, h, d, w, y).
function formatCompactElapsed(totalSec) {
  if (totalSec < 60) {
    return `${Math.max(1, totalSec)}s`;
  }

  const totalMin = Math.floor(totalSec / SECONDS_PER_MINUTE);
  if (totalMin < 60) {
    return `${totalMin}m`;
  }

  const totalHour = Math.floor(totalSec / SECONDS_PER_HOUR);
  if (totalHour < 24) {
    return `${totalHour}h`;
  }

  const totalDay = Math.floor(totalSec / SECONDS_PER_DAY);
  if (totalDay < 7) {
    return `${totalDay}d`;
  }

  const totalWeek = Math.floor(totalSec / SECONDS_PER_WEEK);
  if (totalWeek < 52) {
    return `${totalWeek}w`;
  }

  const totalYear = Math.floor(totalSec / SECONDS_PER_YEAR);
  return `${totalYear}y`;
}

// Format how long ago a timestamp occurred using compact relative units.
export function formatRelativeTime(timestamp) {
  const now = timezone.momentFn().valueOf();
  const then = timezone.momentFn(timestamp).valueOf();

  if (Number.isNaN(then)) {
    return '';
  }

  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  return formatCompactElapsed(diffSec);
}

// Format a millisecond duration into one compact unit.
export function formatDurationCompact(durationMs) {
  const totalSec = Math.max(0, Math.floor(durationMs / 1000));
  return formatCompactElapsed(totalSec);
}

// Format a millisecond duration into up to maxUnits precise units.
export function formatDurationPrecise(durationMs, maxUnits = 3) {
  const totalSec = Math.max(0, Math.floor(durationMs / 1000));
  const nonZeroUnits = getNonZeroDurationUnits(totalSec);
  const unitsToDisplay =
    nonZeroUnits[0]?.[1] === 's' ? nonZeroUnits : nonZeroUnits.filter(([, suffix]) => suffix !== 's');
  const unitLimit = Math.max(1, Math.floor(maxUnits));

  if (unitsToDisplay.length === 0) {
    return '0s';
  }

  return unitsToDisplay
    .slice(0, unitLimit)
    .map(([value, suffix]) => `${value}${suffix}`)
    .join(' ');
}
